import httpClient, { configureKYCTransactionAPIClient, uploadClient } from "./axiosConfig";
import { buildURI } from "./../utils/buildURI";
import { AUTH_CLIENT_ID, AUTH_CLIENT_SECRET, MULTI_PARTNER_PERSONA } from "../constants";

export const OTP_ACTION_GENERATE = "generate";
export const OTP_ACTION_VERIFY = "verify";
export const SEGMENT = "sme";

export const config = {
  load: headers => {
    return httpClient.preAuth.request({
      method: "GET",
      url: "/api/v1/config/stp-accounts",
      ...headers
    });
  },
  postAuthLoad: headers => {
    return httpClient.postAuth.request({
      method: "GET",
      url: "/api/v1/config/stp-accounts",
      ...headers
    });
  }
};

export const dataList = {
  get: segment =>
    httpClient.request({
      url: `webapply/api/v1/datalist?segment=${segment}`,
      method: "GET"
    })
};

export const postAuthToken = {
  get: (payload, headers) =>
    httpClient.postAuth.request({
      url: buildURI("postAuthToken"),
      method: "POST",
      data: payload,
      headers: {
        ...headers,
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: ""
      }
    })
};

export const authentication = {
  login: (data, headers) =>
    httpClient.preAuth.request({
      url: buildURI("authenticateUserUri"),
      method: "POST",
      data,
      ...headers
    }),
  logout: (data, headers) =>
    httpClient.postAuth.request({
      url: buildURI("logoutUri"),
      method: "POST",
      data,
      ...headers
    })
};

export const otp = {
  generate: (payload, headers) =>
    httpClient.preAuth.request({
      url: buildURI("otpUri"),
      method: "POST",
      ...headers,
      data: {
        //action: OTP_ACTION_GENERATE,
        ...payload
      }
    }),
  verify: (payload, headers) =>
    httpClient.preAuth.request({
      url: buildURI("otpUri"),
      method: "POST",
      ...headers,
      data: {
        action: OTP_ACTION_VERIFY,
        ...payload
      }
    })
};

export const prospect = {
  create: ({ applicantInfo, organizationInfo, partnersDetailInfo }, headers) =>
    httpClient.preAuth.request({
      url: buildURI("createProspectUri"),
      method: "POST",
      ...headers,
      data: {
        applicantInfo,
        organizationInfo,
        ...(MULTI_PARTNER_PERSONA.includes(applicantInfo.persona) && { partnersDetailInfo })
      }
    }),
  update: (prospectId, data, headers, isWtm) =>
    httpClient.postAuth.request({
      url: isWtm
        ? buildURI("updateWtmProspectUri", prospectId)
        : buildURI("updateProspectUri", prospectId),
      method: "PATCH",
      ...headers,
      data
    }),
  get: (prospectId, headers, isWtm, tlNo, tlAuth) =>
    httpClient.postAuth.request({
      url: isWtm
        ? buildURI("getWtmProspectUri", prospectId)
        : buildURI("getProspectUri", prospectId),
      method: "GET",
      ...headers,
      params: { ...(tlNo && { tlNo }), ...(tlAuth && { issuingAuthority: tlAuth }) },
      skipOops: Boolean(tlNo && tlAuth)
    }),
  updateSignatory: (prospectId, data, headers, signatoryId, isWtm) =>
    httpClient.postAuth.request({
      url: isWtm
        ? buildURI("updateWtmProspectByShareholderUri", prospectId, null, null, signatoryId)
        : buildURI("updateProspectByShareholderUri", prospectId, null, null, signatoryId),
      method: "PATCH",
      ...headers,
      data
    })
};

export const getProspectDocuments = {
  retriveDocuments: (prospectId, headers) =>
    httpClient.postAuth.request({
      url: buildURI("getProspectDocumentsUri", prospectId),
      method: "GET",
      ...headers
    })
};

export const search = {
  searchApplication: (data, headers) =>
    httpClient.postAuth.request({
      url: buildURI("searchProspectUri"),
      method: "POST",
      ...headers,
      data
    })
};

export const screening = {
  send: prospectId =>
    httpClient.preAuth.request({
      url: buildURI("screenProspectUri", prospectId),
      method: "GET"
    })
};

export const uploadProspectDocument = {
  send: ({ data, prospectId, source, onUploadProgress, headers }) =>
    uploadClient.request({
      url: buildURI("docUploaderUri", prospectId),
      method: "POST",
      ...headers,
      data,
      cancelToken: source.token,
      onUploadProgress
    })
};

export const downloadProspectDocument = {
  get: (prospectId, documentKey, headers) =>
    uploadClient.request({
      url: buildURI("getDocumentByIdUri", prospectId, documentKey),
      method: "GET",
      responseType: "blob",
      ...headers
    })
};

// ro-assist-brd3-1
export const createInvite = {
  send: (data, headers) =>
    httpClient.preAuth.request({
      url: buildURI("createInviteUri"),
      method: "POST",
      ...headers,
      data
    })
};

export const decisions = {
  make: (prospectId, data, headers) =>
    httpClient.postAuth.request({
      url: buildURI("prospectDecisions", prospectId),
      method: "POST",
      ...headers,
      data
    }),
  get: (prospectId, headers, isWtm) =>
    httpClient.postAuth
      .request({
        url: isWtm
          ? buildURI("prospectWtmDecisions", prospectId)
          : buildURI("prospectDecisions", prospectId),
        method: "GET",
        ...headers
      })
      .then(response => {
        return response;
      })
};

export const createKYCTransaction = {
  send: (
    prospectId,
    individualId,
    reuseExistingTransaction,
    isMultipartner,
    individualType,
    headers,
    wtm
  ) =>
    httpClient.postAuth
      .request({
        url: wtm ? buildURI("createWtmKYCTransactionUri") : buildURI("createKYCTransactionUri"), //webapply/products/sme/kyc-transactions
        method: "POST",
        ...headers,
        data: { prospectId, individualId, reuseExistingTransaction, individualType, isMultipartner }
      })
      .then(response => {
        const {
          data: { hostReferenceId, transactionId }
        } = response;
        configureKYCTransactionAPIClient(transactionId, hostReferenceId);
        return response;
      })
};

export const getSdkConfiguration = {
  get: (headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/sdk-configuration`,
        method: "GET",
        ...headers
      })
      .then(response => {
        return response.data.data;
      });
  }
};

export const analyzeOcrData = {
  send: async (transactionId, ocrData, headers, documentType, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/transactions/${transactionId}/ocr`, //webapply/products/sme/kyc-transactions
        method: "POST",
        data: {
          bundleId: "com.rak",
          document: ocrData.docFront,
          documentBack: ocrData.docBack,
          documentType
        },
        ...headers
      })
      .then(response => {
        return response.data;
      });
  },
  getFaceScanKey: (headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/temporary-key`,
        method: "GET",
        ...headers
      })
      .then(response => {
        return response.data;
      });
  },
  postFaceLiveliness: (transactionId, livenessData, headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/transactions/${transactionId}/liveness`,
        method: "POST",
        data: livenessData,
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  },
  validateAndConfirmIdentity: (transactionId, faceData, imageHash, headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/transactions/${transactionId}/identity`,
        method: "POST",
        data: {
          faceData,
          imageHash
        },
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  },
  entityConfirmation: (transactionId, faceData, imageHash, data, headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${
          webMobileRefId ? "wtm/" : ""
        }efr/transactions/${transactionId}/confirm-entity`,
        method: "POST",
        data,
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  },
  notifyHost: (transactionId, signatoryId, headers, webMobileRefId) => {
    const payload = signatoryId ? { signatoryId } : {};
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${
          webMobileRefId ? "wtm/" : ""
        }efr/transactions/${transactionId}/notify-host`,
        method: "PATCH",
        data: payload,
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  }
};

export const getOCRDataStatus = {
  getOCRStatus: (transactionId, headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${webMobileRefId ? "wtm/" : ""}efr/transactions/${transactionId}/stages`,
        method: "GET",
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  },
  getOCRStageData: (transactionId, stage, headers, webMobileRefId) => {
    return httpClient.postAuth
      .request({
        url: `api/v1/bbg/${
          webMobileRefId ? "wtm/" : ""
        }efr/transactions/${transactionId}/stages/${stage}/details`,
        method: "GET",
        ...headers
      })
      .then(function(response) {
        return response.data;
      });
  }
};

export const documents = {
  requestClientToken: headers =>
    httpClient.postAuth.request({
      url: buildURI("documentUploaderToken"),
      method: "POST",
      data: {
        client_id: AUTH_CLIENT_ID,
        client_secret: AUTH_CLIENT_SECRET,
        module_id: "DAO"
      },
      headers: { ...headers?.headers, "Content-Type": "application/x-www-form-urlencoded" }
    }),
  getDocumentList: (prospectId, headers) =>
    httpClient.postAuth.request({
      url: buildURI("getProspectDocumentsUri", prospectId),
      method: "GET",
      ...headers
    }),
  upload: (fileData, jwtToken, prospectId, headers) => {
    const data = new FormData();
    data.append("documentType", fileData.documentType);
    data.append("documentTitle", fileData.documentTitle);
    data.append("fileName", fileData.fileName);
    data.append("fileFormat", fileData.fileFormat);
    data.append("fileSize", fileData.fileSize);
    data.append("jwt", jwtToken);
    data.append("uploadfile", fileData.file);
    return uploadClient.request({
      url: buildURI("uploadDocumentUri", prospectId),
      method: "POST",
      ...headers,
      data
    });
  }
};

export const webToMobile = {
  generateQRCode: (prospectId, data, headers) =>
    httpClient.postAuth
      .request({
        url: buildURI("generateWebToMobileQRCode", prospectId),
        method: "POST",
        ...headers,
        data
      })
      .then(response => {
        return response.data;
      })
      .catch(err => {
        console.log("error while generating QR code", err.message);
      }),
  refreshQRCode: (prospectId, webToMobileRefId, data, headers) =>
    httpClient.postAuth
      .request({
        url: buildURI("refreshWebToMobileQRCode", prospectId, null, webToMobileRefId),
        method: "POST",
        ...headers,
        data
      })
      .then(response => {
        return response.data;
      })
      .catch(err => console.log("error while refreshing QR code", err.message)),
  wtmSyncSession: (data, headers) =>
    httpClient.postAuth.request({
      url: buildURI("wtmSyncSession"),
      method: "POST",
      ...headers,
      data
    }),
  wtmStatusUpdate: (data, headers, prospectId, webtobomrefId) => {
    return httpClient.postAuth.request({
      url: buildURI("wtmStatusUpdate", prospectId, "", webtobomrefId),
      method: "PATCH",
      ...headers,
      skipOops: true,
      data
    });
  },
  checkQRCodeStatus: (prospectId, webToMobileRefId, header) =>
    httpClient.postAuth
      .request({
        url: buildURI("wtmStatusUpdate", prospectId, null, webToMobileRefId),
        method: "GET",
        ...header
      })
      .then(response => {
        return response.data;
      })
      .catch(err => console.log("error while calling poll", err.message))
};

export const kfsAcknowledgement = {
  sendMail: (body, headers) =>
    httpClient.postAuth
      .request({
        url: buildURI("cpfSendEmailUrl"),
        method: "POST",
        ...headers,
        data: body
      })
      .then(response => {
        return response.data;
      })
};

export const sendEFRInvite = {
  sendMail: (prospectId, body, headers) =>
    httpClient.postAuth.request({
      url: buildURI("sendEFRInvite", prospectId),
      method: "POST",
      ...headers,
      data: body
    })
};

export const cpfCustomerConsent = {
  send: (body, headers) =>
    httpClient.postAuth
      .request({
        url: buildURI("cpfCustomerConsentUrl"),
        method: "POST",
        ...headers,
        data: body
      })
      .then(response => {
        return response.data;
      })
};
